import { useTranslation } from "react-i18next";
import { Skeleton } from "@mui/material";
import { IconCheck, IconChevronRight, IconSearch, IconUsersGroup, IconX } from "@tabler/icons-react";

import { Button, Input, Text } from "@fragments";
import { CommunityBanner } from "@components/Community";

/**
 * Step 1 — pick the group to post in. One flat list of groups (each thumb
 * carries its community as a corner badge) under a sticky toolbar: a search
 * over group + community name and one filter pill per community. Choosing a
 * group advances the flow immediately.
 */
export default function GroupPicker({
    loading,
    groups,
    total,
    communities,
    search,
    communityFilter,
    onSearchChange,
    onToggleCommunity,
    onClearCommunities,
    onClearFilters,
    selectedId,
    onSelect,
}) {
    const { t } = useTranslation("groupModals");

    // Nothing to pick from at all — no toolbar either.
    if (!loading && total === 0) {
        return (
            <div className="group-picker__empty">
                <span className="group-picker__empty-icon" aria-hidden="true">
                    <IconUsersGroup />
                </span>
                <Text.label contrast={9}>
                    {t("publishPost.selectGroup.emptyTitle")}
                </Text.label>
                <Text.span contrast={6}>
                    {t("publishPost.selectGroup.emptySubtitle")}
                </Text.span>
            </div>
        );
    }

    return (
        <div className="group-picker">
            <div className="group-picker__toolbar">
                <Input
                    className="publish-post-search"
                    placeholder={t("publishPost.selectGroup.searchPlaceholder")}
                    start={<IconSearch />}
                    value={search}
                    onInput={onSearchChange}
                    fullWidth
                />

                {loading ? (
                    <FilterSkeleton />
                ) : (
                    communities.length > 1 && (
                        <div
                            className="group-filter"
                            role="group"
                            aria-label={t("publishPost.selectGroup.filterLabel")}
                        >
                            {communityFilter.length > 0 && (
                                <span className="group-filter__clear">
                                    <button
                                        type="button"
                                        className="group-filter__pill group-filter__pill--clear"
                                        aria-label={t("publishPost.selectGroup.clearFilters")}
                                        onClick={onClearCommunities}
                                    >
                                        <IconX />
                                    </button>
                                </span>
                            )}

                            {communities.map((community) => {
                                const active = communityFilter.includes(String(community.id));
                                return (
                                    <button
                                        key={community.id}
                                        type="button"
                                        aria-pressed={active}
                                        className={`group-filter__pill${active ? " group-filter__pill--active" : ""}`}
                                        onClick={() => onToggleCommunity(community.id)}
                                    >
                                        <CommunityBanner size={22} banner={community.banner} />
                                        <Text.span contrast={8} className="group-filter__label">
                                            {community.name || t("common:entities.community")}
                                        </Text.span>
                                    </button>
                                );
                            })}
                        </div>
                    )
                )}
            </div>

            {loading ? (
                <ListSkeleton />
            ) : groups.length ? (
                <ul
                    className="group-picker__list"
                    role="listbox"
                    aria-label={t("publishPost.steps.group")}
                >
                    {groups.map((group, index) => (
                        <GroupOption
                            key={group.id}
                            group={group}
                            index={index}
                            selected={selectedId === group.id}
                            onSelect={() => onSelect(group)}
                        />
                    ))}
                </ul>
            ) : (
                <div className="group-picker__empty group-picker__empty--results">
                    <Text.label contrast={9}>
                        {t("publishPost.selectGroup.noResults")}
                    </Text.label>
                    <Button
                        variant="text"
                        color="inverse"
                        size="small"
                        onClick={onClearFilters}
                        preventLoading
                    >
                        {t("publishPost.selectGroup.clearFilters")}
                    </Button>
                </div>
            )}
        </div>
    );
}

function GroupOption({ group, index, selected, onSelect }) {
    const { t } = useTranslation("groupModals");

    const community = group.community || {};
    const posts = t("community:group.posts", { count: group.posts_total || 0 });
    const caption = community.name ? `${community.name} · ${posts}` : posts;

    function handleKeyDown(event) {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect();
        }
    }

    return (
        <li
            role="option"
            aria-selected={selected}
            tabIndex={0}
            className={`pp-choice group-picker__item${selected ? " pp-choice--selected" : ""}`}
            style={{ "--i": index }}
            onClick={onSelect}
            onKeyDown={handleKeyDown}
        >
            <span className="pp-choice__media pp-choice__media--stacked" aria-hidden="true">
                <span className="pp-choice__thumb">
                    {group.banner ? <img src={group.banner} alt="" /> : <IconUsersGroup />}
                </span>
                <span className="pp-choice__badge">
                    <CommunityBanner size={18} banner={community.banner} />
                </span>
            </span>

            <div className="pp-choice__text">
                <Text.label contrast={9} className="pp-choice__title">
                    {group.name}
                </Text.label>
                <Text.caption contrast={6} className="pp-choice__caption">
                    {caption}
                </Text.caption>
            </div>

            <span className="pp-choice__trail" aria-hidden="true">
                {selected ? <IconCheck /> : <IconChevronRight />}
            </span>
        </li>
    );
}

// Two pill-shaped bars where the community filters will land.
function FilterSkeleton() {
    return (
        <div className="group-filter" aria-hidden="true">
            {[128, 104].map((width, i) => (
                <Skeleton
                    key={i}
                    variant="rounded"
                    width={width}
                    height={30}
                    animation="wave"
                    className="pp-skeleton pp-skeleton--pill"
                />
            ))}
        </div>
    );
}

// Rows of thumb + two text bars, mirroring the real anatomy so the list
// doesn't jump when data lands.
function ListSkeleton() {
    return (
        <div className="group-picker__list" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, i) => (
                <div
                    key={i}
                    className="pp-choice pp-choice--skeleton group-picker__item"
                    style={{ "--i": i }}
                >
                    <Skeleton
                        variant="rounded"
                        width={40}
                        height={40}
                        animation="wave"
                        className="pp-skeleton pp-skeleton--media"
                    />
                    <div className="pp-choice__text">
                        <Skeleton variant="text" width="44%" height={14} animation="wave" className="pp-skeleton" />
                        <Skeleton variant="text" width="30%" height={11} animation="wave" className="pp-skeleton" />
                    </div>
                </div>
            ))}
        </div>
    );
}
